import { forwardRef, useCallback, useState } from 'react';

import { getSourceEventParams, useAmplitude } from '@crehana/analytics';
import MyAssignmentCoursesWidget, {
  type TMyAssignmentCourseItem,
} from '@crehana/compass.dashlets/my-assignment-courses-widget';
import { createUrlWithSourceQueryParams } from '@crehana/utils';

import { useMyAssignedCoursesQuery } from '../hooks/use-my-assigned-courses-query/use-my-assigned-courses-query';

type TMyAssignedCoursesWidgetProps = {
  dataTestId?: string;
  amplitudeSource: string;
  organizationSlug: string;
};

const MyAssignedCoursesWidget = forwardRef<
  HTMLDivElement,
  TMyAssignedCoursesWidgetProps
>(
  (
    { dataTestId, amplitudeSource, organizationSlug },
    ref: React.ForwardedRef<HTMLDivElement>,
  ) => {
    const { logEvent } = useAmplitude();
    const [search, setSearch] = useState('');
    const {
      courses,
      coursesStarted,
      error,
      loading: loadingCourses,
      loadingMore: loadingMoreCourses,
      refetch: refetchCourses,
      fetchMoreCourses,
    } = useMyAssignedCoursesQuery({
      search,
    });

    const handleViewAllCourses = useCallback(() => {
      window.location.href = createUrlWithSourceQueryParams({
        url: `/org/${organizationSlug}/learn/my-courses/enrolled/`,
        source: amplitudeSource,
      });
    }, [amplitudeSource, organizationSlug]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentionally managed
    const handleSelectCourse = useCallback(
      (course: TMyAssignmentCourseItem, order: number) => {
        logEvent({
          event: 'HomeUser Action Course Continued',
          data: {
            ...getSourceEventParams(),
            'Course Order': order,
            'Course Id': course.id,
            'Course Progress': course.progress,
          },
        });
        window.location.href = createUrlWithSourceQueryParams({
          url: `/clases/v2/${course.id}/player/`,
          source: amplitudeSource,
        });
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [amplitudeSource],
    );

    return (
      <div ref={ref} data-testid={dataTestId} className="tw-w-full tw-h-full">
        <MyAssignmentCoursesWidget
          courses={courses}
          isLoading={loadingCourses}
          isError={!!error}
          isLoadingMore={loadingMoreCourses}
          onSearch={setSearch}
          onRetry={() => refetchCourses()}
          onLoadMore={fetchMoreCourses}
          coursesStarted={coursesStarted}
          onSelectCourse={handleSelectCourse}
          onViewAllCourses={handleViewAllCourses}
        />
      </div>
    );
  },
);

MyAssignedCoursesWidget.displayName = 'MyAssignedCoursesWidget';

export { MyAssignedCoursesWidget };
